import React, { useState, useEffect, useMemo } from 'react';
import { TextInput, Popover, Text, Divider, Flex, Checkbox } from '@mantine/core';
import debounce from 'lodash/debounce';
import { ExtendedColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { Z_INDEX } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { FILTER_DEBOUNCE_MS } from '@dealstack/frontend/shared/components/Table/MantineTable/constants';

interface FilterSection {
	title: string;
	fieldKey: string;
	options: Array<{ value: string; label: string }>;
}

interface Props<TData, TValue> {
	column: ExtendedColumn<TData, TValue>;
	filterText?: string;
	nameFieldKey: string;
	sections: FilterSection[];
}

type FilterValue = Record<string, string | string[] | undefined>;

export const PersonFilterWithSearch = <TData, TValue>({
	column,
	filterText,
	nameFieldKey,
	sections,
}: Props<TData, TValue>) => {
	const { getFilterValue, setFilterValue } = column;
	const [popoverOpened, setPopoverOpened] = useState(false);

	const currentValue = (getFilterValue() as FilterValue) || {};

	const searchText = (currentValue[nameFieldKey] as string) ?? '';
	const [localSearchText, setLocalSearchText] = useState(searchText);

	useEffect(() => {
		setLocalSearchText(searchText);
	}, [searchText]);

	const applyFilterValue = (current: FilterValue, updates: Partial<FilterValue>) => {
		const newValue: FilterValue = { ...current, ...updates };
		Object.keys(newValue).forEach((key) => {
			if (newValue[key] === undefined) {
				delete newValue[key];
			}
		});
		setFilterValue(Object.keys(newValue).length > 0 ? newValue : null);
	};

	const debouncedApplyNameFilter = useMemo(
		() =>
			debounce((name: string) => {
				const current = (getFilterValue() as FilterValue) || {};
				const updated = { ...current, [nameFieldKey]: name || undefined };
				const cleaned = Object.fromEntries(
					Object.entries(updated).filter(([, v]) => v !== undefined)
				);
				setFilterValue(Object.keys(cleaned).length > 0 ? cleaned : null);
			}, FILTER_DEBOUNCE_MS),
		[getFilterValue, nameFieldKey, setFilterValue]
	);

	useEffect(() => () => debouncedApplyNameFilter.cancel(), [debouncedApplyNameFilter]);

	const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const name = event.currentTarget.value;
		setLocalSearchText(name);
		debouncedApplyNameFilter(name);
		setPopoverOpened(false);
	};

	const handleSectionChange = (fieldKey: string, values: string[]) => {
		applyFilterValue(currentValue, { [fieldKey]: values.length > 0 ? values : undefined });
	};

	const handleCheckboxToggle = (fieldKey: string, optionValue: string) => {
		const currentValues = (currentValue[fieldKey] as string[]) ?? [];
		const newValues = currentValues.includes(optionValue)
			? currentValues.filter((v) => v !== optionValue)
			: [...currentValues, optionValue];
		handleSectionChange(fieldKey, newValues);
	};

	const handleClearSearch = (event: React.MouseEvent) => {
		event.stopPropagation();
		setLocalSearchText('');
		debouncedApplyNameFilter.cancel();
		applyFilterValue(currentValue, { [nameFieldKey]: undefined });
	};

	return (
		<Popover
			opened={popoverOpened}
			onChange={setPopoverOpened}
			position="bottom-start"
			withinPortal
			shadow="md"
			width="target"
			styles={{
				dropdown: {
					zIndex: Z_INDEX.MENU,
				},
			}}
		>
			<Popover.Target>
				<TextInput
					placeholder={filterText ?? 'Search by name'}
					value={localSearchText}
					onChange={handleNameChange}
					onClick={() => setPopoverOpened(true)}
					size="sm"
					rightSection={
						localSearchText ? (
							<Icon
								variant="close-v3"
								size="md"
								onClick={handleClearSearch}
								style={{ cursor: 'pointer' }}
							/>
						) : (
							<Icon variant="caret-down" size="lg" />
						)
					}
					styles={{
						input: {
							borderColor: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
							cursor: 'pointer',
							'&:hover': {
								borderColor: defaultTheme.v3.colors.dark[0],
							},
							'&:focus': {
								borderColor: defaultTheme.v3.colors.dark[0],
							},
							'&:focus-within': {
								borderColor: defaultTheme.v3.colors.dark[0],
							},
						},
					}}
				/>
			</Popover.Target>
			<Popover.Dropdown p="0.25rem">
				<Flex gap="0.25rem" direction="column">
					{sections.map((section, index) => (
						<React.Fragment key={section.fieldKey}>
							{index > 0 && <Divider color={defaultTheme.v3.colors.gray[2]} />}
							<div>
								<div style={{ padding: '0.5rem' }}>
									<Text
										mb="0"
										size="0.75rem"
										fw={400}
										c={defaultTheme.v3.colors.gray[9]}
									>
										{section.title}
									</Text>
								</div>
								<Flex gap="0.25rem" direction="column">
									{section.options.map((option) => {
										const isChecked = (
											(currentValue[section.fieldKey] as string[]) ?? []
										).includes(option.value);
										return (
											<Checkbox
												key={option.value}
												label={option.label}
												checked={isChecked}
												onChange={(event) => {
													event.stopPropagation();
													handleCheckboxToggle(
														section.fieldKey,
														option.value
													);
												}}
												size="0.875rem"
												styles={{
													body: {
														alignItems: 'center',
														padding: '0.5rem',
													},
													input: {
														border: `1px solid ${defaultTheme.v3.colors.dark[4]} !important`,
														borderRadius: '0.125rem !important',
														':checked': {
															backgroundColor:
																defaultTheme.v3.colors.blue[6],
															border: 'none !important',
														},
													},
													label: {
														fontSize: '0.875rem',
														fontWeight: 400,
													},
												}}
											/>
										);
									})}
								</Flex>
							</div>
						</React.Fragment>
					))}
				</Flex>
			</Popover.Dropdown>
		</Popover>
	);
};
